import { customElement, property } from 'lit/decorators.js';
import { LitElement, html, nothing } from 'lit';
import { style } from './round-button.styles';
import {SbkModal} from "@/components/modal/modal.ts";

@customElement('sbk-round-button')
export class SbkRoundButton extends LitElement {

  static formAssociated = true;

  _internals: ElementInternals;

  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

  @property({ type: Boolean })
  active = false;

  @property({ type: Boolean })
  disabled = false;

  @property({ type: Boolean })
  error = false;

  @property({ type: String })
  href = '';

  @property({type: String})
  icon = '';

  @property()
  size?: 'extra-large' | 'large' | 'medium' | 'small' | 'extra-small' = 'large';

  @property()
  target?: '_blank' | '_parent' | '_self' | '_top' | '' = '';

  @property({type: String})
  value? = '';

  @property()
  variant: 'default' | 'icon-only' | 'text' = 'default';

  @property()
  type: 'button' | 'submit' = 'button';

  @property({type: Boolean, attribute: 'is-ajax'})
  isAjax = false;

  @property({attribute: 'modal-trigger-id'})
  modalTriggerId = ''

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.modalTriggerId) {
      this.addEventListener('click', this._showModal)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._showModal)
  }

  static get styles() {
    return style;
  }

  render() {
    return this.href ? this.renderLink() : this.renderButton();
  }

  private renderButton() {
    return html`
      <button
          id="button"
          part="button"
          class="round-button round-button-${this.size} round-button-${this.variant}${this.error ? ' round-button--error' : ''}${this.active ? ' round-button--active' : ''}"
          aria-labelledby="button-label"
          type=${this.type}
          tabindex="0"
          @click=${this.type === "submit" ? this._handleSubmit : nothing}
          ?disabled=${this.disabled}>
        ${this.renderIcon()}
        <span id="button-label" ?hidden=${this.variant !== 'text'}>
          <slot class="round-button__content"></slot>
        </span>
      </button>
    `;
  }

  private renderLink() {
    return html`
      <a ?id="link"
         part="link"
         class="round-button round-button-${this.size} round-button-${this.variant}${this.error ? ' round-button--error' : ''}${this.active ? ' round-button--active' : ''}"
         role="button"
         href=${this.href}
         ?title=${this.title}
         target=${this.target}
         ?disabled=${this.disabled}
         rel=${this.isAjax ? 'nofollow' : nothing}
      >
        ${this.renderIcon()}
        <span id="button-label" ?hidden=${this.variant !== 'text'}>
          <slot class="round-button__content"></slot>
        </span>
      </a>
    `;
  }

  private renderIcon() {
    if (this.variant === 'text') {
      return html`${nothing}`;
    }
    return html`
      <slot class="round-button__icon-slot" name="icon">
        <div class="icon-wrapper" part="icon">
          <sbk-icon size=${this.getIconSize(this.size)} symbol=${this.icon}></sbk-icon>
        </div>
      </slot>
    `;
  }

  private getIconSize(size: 'small' | 'medium' | 'large' | 'extra-large' | 'extra-small' | undefined): number {
    switch (size) {
      case 'extra-small':
        return 16;
      case 'small':
        return 20;
      case 'medium':
      case 'large':
        return 24;
      default:
        return 24;
    }
  }

  _handleSubmit() {
    const clickEvent = new CustomEvent('round-button-submit');
    this.dispatchEvent(clickEvent);
    this._internals.form?.requestSubmit();
  }

  _showModal(e: Event) {
    e.preventDefault();

    const modal = document.querySelector(`#${this.modalTriggerId}`) as SbkModal
    if (modal) {
      modal.showModal()
    }
  }
}
